import React, { useMemo } from 'react'

import { Stack, Typography } from '@mui/material'

import {
  CertificateGroupMemberPreview,
  CertificateGroupMemberPreviewSkeleton,
} from 'components/display/CertificateGroupMemberPreview'
import { useCertificateGroup, type CertificateGroupData } from 'hooks/api'
import { isActivateAvailable, isCertificateGroupSplittable } from 'utils'
import type { CertificateAdmin } from 'types'

import { ConfirmSettingModal } from '../ConfirmSettingModal'

/**
 * VRG-231 — the confirmation gate on "Activate".
 *
 * WHY THIS IS A GATE AND NOT A SAVE BUTTON (working-conventions §43): activation
 * is the BILLABLE event and it is one-way. A Save button can gate a change but
 * structurally cannot say what the change costs, and cost is the entire thing a
 * user needs to know here. So this modal's job is to name three facts a button
 * cannot: what activation means, how many certificates it takes live, and how
 * many activations that is charged as.
 *
 * WHY THE COUNT IS COMPUTED AND NOT HEDGED: an indivisible group cascades, so
 * clicking "Activate" on one member takes the whole group live. The two pages
 * that used to own this each hand-rolled a modal whose entire group treatment
 * was one sentence — "This is an indivisible group. Activating it will
 * automatically activate all of its children too." — with no number, and shown
 * only on the PARENT. Both halves were wrong after the cascade was made
 * symmetric: a member cascades now too, and "all of its children" is a quantity
 * the user is entitled to before spending money, not after. The number here is
 * derived from real group membership.
 *
 * WHY IT IS SHARED: CertificatesPage and CertificatePage had two near-identical
 * copies of this modal, and they had already drifted (the list's copy keyed its
 * group warning off a local `isGroupParentRow` helper, the detail page's off a
 * `groupRole` variable). That is the §36 trigger — second hand-roll, extract —
 * and it is why the copy now lives in one place rather than in two files that
 * must be edited together and were not.
 */

export interface ActivationScope {
  /** Certificates this click takes live, including the one clicked. Always >= 1. */
  totalActivating: number
  /**
   * The OTHER members that will also activate. Only the eligible ones: a member
   * that is already live is not activated again, so it is not in this list and
   * is not in `totalActivating`.
   */
  cascadingMembers: CertificateAdmin[]
  /** Other members already live. They do not change and are not re-charged. */
  alreadyLiveCount: number
  isIndivisibleGroup: boolean
  isDivisibleGroup: boolean
  /**
   * The group is still splittable TODAY, so this activation is what freezes it.
   *
   * Checked rather than assumed: an indivisible group whose members are already
   * live is ALREADY permanent, and telling that user "this will make the group
   * permanent" describes a consequence their action does not have. The warning
   * is only true the first time.
   */
  freezesGroup: boolean
}

/**
 * Pure, so the arithmetic and the wording below are checkable without mounting
 * a modal or mocking react-query — the same reason `BulkActivationModal`
 * exported `getBulkActivationCascadeWarning`.
 */
export const getActivationScope = (params: {
  certificate: CertificateAdmin | null
  groupData: CertificateGroupData | null
}): ActivationScope => {
  const { certificate, groupData } = params

  const divisibility = certificate?.group_divisibility ?? null
  const isIndivisibleGroup = divisibility === 'indivisible'
  const isDivisibleGroup = divisibility === 'divisible'

  // `[parent, ...children]` is the WHOLE group from either side: the endpoint
  // resolves it identically whether the requested certificate is the tagless
  // parent or one of its children (see GetCertificateGroupUseCase). Filtering
  // self out by id is what makes this work from both sides without branching on
  // which side we are.
  const allMembers = [groupData?.parent, ...(groupData?.children ?? [])].filter(
    (member): member is CertificateAdmin => Boolean(member)
  )
  const otherMembers = certificate
    ? allMembers.filter(member => member.id !== certificate.id)
    : allMembers

  // Only an indivisible group cascades. A divisible group's members activate
  // (and bill) one at a time, which is the whole difference between the two.
  const cascadingMembers = isIndivisibleGroup
    ? otherMembers.filter(member => isActivateAvailable(member.status))
    : []
  const alreadyLiveCount = isIndivisibleGroup
    ? otherMembers.length - cascadingMembers.length
    : 0

  return {
    totalActivating: 1 + cascadingMembers.length,
    cascadingMembers,
    alreadyLiveCount,
    isIndivisibleGroup,
    isDivisibleGroup,
    freezesGroup:
      isIndivisibleGroup && allMembers.length > 0 && isCertificateGroupSplittable(allMembers),
  }
}

/**
 * The modal's body copy, as an ordered list of short sentences.
 *
 * Returned as strings rather than JSX so the wording is assertable directly.
 *
 * WRITTEN SHORT ON PURPOSE, and it took a rewrite to get there. The first
 * version stated the same four facts in three paragraphs (~70 words) and read
 * as a wall, which defeats the point: a consequence nobody finishes reading is
 * not a consequence that was communicated. What the cut removed was everything
 * the user can already see or does not need at this instant:
 *
 * - "all N go live" — the TITLE already says "Activate N certificates?".
 * - "for anyone with access" — true, and not the question being decided.
 * - "indivisible, so its members are not separate certificates. Activating any
 *   one of them activates all N" — one idea said twice.
 * - "Activation is the billable event, and the whole group is charged as..." —
 *   scaffolding around a number. The number is the fact.
 *
 * TWO FACTS ARE LOAD-BEARING AND MUST SURVIVE ANY FURTHER EDIT: the CHARGE
 * COUNT, and that activating permanently locks an indivisible group from being
 * split. Everything else here is negotiable; those two are why the modal exists.
 *
 * Periods over em-dashes (§15).
 */
export const getActivationConsequenceLines = (scope: ActivationScope): string[] => {
  const { totalActivating, alreadyLiveCount, isIndivisibleGroup, isDivisibleGroup, freezesGroup } =
    scope
  const lines: string[] = []

  if (isIndivisibleGroup && totalActivating > 1) {
    lines.push(
      `Indivisible group. Activating one activates all ${totalActivating}, charged as 1 activation.`
    )
  } else if (isIndivisibleGroup && alreadyLiveCount > 0) {
    // Nothing cascades because the rest of the group is already live. Worth one
    // clause: it explains why the count is 1 on a certificate the user knows is
    // part of a group, which would otherwise look like the modal miscounted.
    lines.push('Indivisible group. Every other member is already live. Charged as 1 activation.')
  } else if (isIndivisibleGroup) {
    lines.push('Indivisible group. Charged as 1 activation.')
  } else if (isDivisibleGroup) {
    lines.push('Divisible group. This certificate activates on its own, charged as 1 activation.')
  } else {
    lines.push('Goes live immediately. Charged as 1 activation.')
  }

  if (freezesGroup) {
    lines.push('Once live, the group can never be split.')
  }

  return lines
}

export interface ActivateCertificateModalProps {
  certificate: CertificateAdmin | null
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
}

export const ActivateCertificateModal = ({
  certificate,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: ActivateCertificateModalProps) => {
  const isGrouped = Boolean(certificate?.group_divisibility)

  // Only fetched for a grouped certificate, and only while the modal is open.
  // Shares `useCertificateGroup`'s query key, so a group already expanded on the
  // list or open on the detail page resolves from a warm cache with no request.
  const { data: groupData, isLoading: isGroupLoading } = useCertificateGroup(
    { certificateId: certificate?.id ?? '' },
    { enabled: open && isGrouped && Boolean(certificate?.id) }
  )

  const scope = useMemo(
    () => getActivationScope({ certificate, groupData }),
    [certificate, groupData]
  )

  const isCountPending = isGrouped && isGroupLoading
  const lines = getActivationConsequenceLines(scope)

  const consequence = (
    <Stack spacing={2}>
      {isCountPending ? (
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          Checking how many certificates this will activate.
        </Typography>
      ) : (
        lines.map(line => (
          <Typography key={line} variant="body2" sx={{ lineHeight: 1.5 }}>
            {line}
          </Typography>
        ))
      )}
    </Stack>
  )

  const confirmLabel =
    scope.totalActivating > 1
      ? `Activate ${scope.totalActivating} certificates`
      : 'Activate certificate'

  return (
    <ConfirmSettingModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={
        scope.totalActivating > 1
          ? `Activate ${scope.totalActivating} certificates?`
          : 'Activate this certificate?'
      }
      consequence={consequence}
      confirmLabel={confirmLabel}
      loading={isLoading}
      // The count IS the decision. Confirming before it resolves would put the
      // user back in exactly the position this modal exists to get them out of,
      // so the confirm waits. §46: a disabled control must say why, and this one
      // does.
      confirmDisabled={isCountPending}
      confirmTooltip={
        isCountPending ? 'Checking how many certificates this will activate.' : undefined
      }
    >
      <Stack spacing={3}>
        {isCountPending && isGrouped && (
          <Stack spacing={3}>
            <CertificateGroupMemberPreviewSkeleton size={48} />
            <CertificateGroupMemberPreviewSkeleton size={48} />
          </Stack>
        )}

        {!isCountPending && scope.cascadingMembers.length > 0 && (
          <Stack spacing={3}>
            <Typography variant="body3" color="text.secondary">
              Also activating ({scope.cascadingMembers.length})
            </Typography>
            {/* Capped so a large group cannot push the confirm button off
                screen. The remainder is counted, never silently dropped. */}
            <Stack spacing={3} sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {scope.cascadingMembers.slice(0, 5).map(member => (
                <CertificateGroupMemberPreview
                  key={member.id}
                  certificate={member}
                  role={member.parent_certificate_id ? 'child' : 'parent'}
                  size={48}
                  // Read-only: evidence, not a destination. A link here would
                  // navigate away mid-decision. See the prop's own note.
                  disabled
                />
              ))}
              {scope.cascadingMembers.length > 5 && (
                <Typography variant="body3" color="text.secondary">
                  and {scope.cascadingMembers.length - 5} more
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </ConfirmSettingModal>
  )
}

export default ActivateCertificateModal
